import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { BOSS_AGGREGATES } from '../../lib/bossLayoutOps'
import { isRowFieldPinned } from '../../lib/bossRowPins'
import type { BossRowPins } from '../../lib/bossRowPins'
import type { BossAggregate, BossFieldArea, BossGridField } from '../../lib/bossLayoutOps'
import '../../styles/boss-fieldbar.css'

// ── Zones ─────────────────────────────────────────────────────────────

type Zone = { area: BossFieldArea; label: string }

/**
 * Left to right, and also the order the left and right arrow keys walk.
 *
 * The labels are the legacy grid's words, not Perspective's: the zone labelled **Columns** is
 * `split_by`, and the one labelled **Values** is `columns` (gap analysis S1). Half of what makes
 * the stock settings sidebar unreadable to a BOSS user is that vocabulary, so the mapping is
 * translated here once and never reaches the screen.
 *
 * The bar sits ABOVE the grid rather than being the grid's own headers (C1): Perspective's header
 * cells cannot be made drag sources, so this is as close to the legacy in-grid areas as the engine
 * allows.
 */
const ZONES: Zone[] = [
    { area: 'group_by', label: 'Rows' },
    { area: 'split_by', label: 'Columns' },
    { area: 'columns', label: 'Values' },
    { area: 'available', label: 'Available' },
]

const AREA_ORDER: BossFieldArea[] = ZONES.map(z => z.area)

/**
 * A MARGIN/ROI expression is a weighted mean over already-aggregated measures, so Perspective has
 * no values to group or split on (C16) — it may summarise, never nest. `moveField` enforces this
 * too, by returning the config unchanged, but a control that silently does nothing is worse than
 * one that refuses visibly: the bar stands the illegal zones down while such a field is in the air
 * and never offers them to the arrow keys.
 */
const DERIVED_AREAS: BossFieldArea[] = ['columns', 'available']

const areasFor = (field: BossGridField): BossFieldArea[] => (field.isDerived ? DERIVED_AREAS : AREA_ORDER)

/** Perspective's aggregate names as a report reader would say them. */
const AGGREGATE_LABELS: Record<BossAggregate, string> = {
    'sum': 'Sum',
    'count': 'Count',
    'distinct count': 'Distinct count',
    'avg': 'Average',
    'min': 'Minimum',
    'max': 'Maximum',
    'median': 'Median',
    'first': 'First',
    'last': 'Last',
    'any': 'Any',
    'var': 'Variance',
    'stddev': 'Std deviation',
}

// ── Drop arithmetic ───────────────────────────────────────────────────

/** The config position just past a zone's last placed entry — where an append belongs. */
const endOf = (inArea: BossGridField[]) => inArea.reduce((max, f) => Math.max(max, f.index), -1) + 1

/**
 * The index to hand `moveField` when `dragged` is dropped on `before` (or at the end of the zone
 * when that is null).
 *
 * Two corrections, each of which produces a visibly wrong drop if skipped:
 *
 *  1. The arithmetic is in CONFIG positions, not chip ordinals, because `BossGridField.index` is
 *     the position inside `group_by` / `split_by` / `columns` and `moveField` indexes that same
 *     array. A config can hold entries the bar never draws — a column an imported layout names
 *     that the report no longer returns, or a field placed in two areas at once, both of which
 *     `inventory()` drops — and counting chips would then insert on the wrong side of them.
 *  2. `moveField` removes the dragged entry BEFORE it inserts, so within one zone everything past
 *     it has already slid down a place: dropping A on C in [A, B, C] is index 1, not 2.
 *
 * The 'available' zone is not ordered — `moveField` ignores the index there — so the number this
 * returns for it is arbitrary but harmless.
 */
export function dropIndex(inArea: BossGridField[], dragged: BossGridField, before: string | null): number {
    const sameArea = inArea.some(f => f.name === dragged.name)
    const target = before === null ? undefined : inArea.find(f => f.name === before)
    const at = target ? target.index : endOf(inArea)
    const shift = sameArea && dragged.index >= 0 && dragged.index < at ? 1 : 0
    return Math.max(0, at - shift)
}

// ── Bar ───────────────────────────────────────────────────────────────

export type FieldBarProps = {
    fields: BossGridField[]
    /** Which Rows fields are frozen and which scroll. Rows chips carry a pin toggle when given. */
    rowPins?: BossRowPins
    onMove: (name: string, area: BossFieldArea, index: number) => void
    onAggregate: (name: string, aggregate: BossAggregate | null) => void
    onPin?: (name: string, pinned: boolean) => void
    /** Disabled while a run is in flight. */
    busy?: boolean
}

/**
 * The legacy pivot grid's four field areas, ported (gap analysis rank 1 — C1, C15, S1).
 *
 * Drag and drop is raw HTML5 DnD with the dragged field name held in React state, the pattern
 * ShortcutPanel and PublicRoadmapPage already use for chips and cards. No DnD library.
 *
 * The bar holds no config of its own: it draws whatever `inventory()` hands down and reports every
 * gesture through `onMove` / `onAggregate`, so the grid and the bar cannot drift apart.
 */
export default function FieldBar({ fields, rowPins, onMove, onAggregate, onPin, busy }: FieldBarProps) {
    const [dragging, setDragging] = useState<string | null>(null)
    const [dropArea, setDropArea] = useState<BossFieldArea | null>(null)
    const [dragOverName, setDragOverName] = useState<string | null>(null)

    const hintId = useId()
    const chips = useRef(new Map<string, HTMLDivElement>())
    const pendingFocus = useRef<string | null>(null)

    const byArea = useMemo(() => {
        const out: Record<BossFieldArea, BossGridField[]> = {
            group_by: [], split_by: [], columns: [], available: [],
        }
        for (const f of fields) out[f.area].push(f)
        // inventory() already hands them over in field-bar order; sorting by index keeps the bar
        // honest if that ever changes. Every 'available' entry carries -1, so a stable sort leaves
        // that zone alphabetical, which is how it arrives.
        for (const area of AREA_ORDER) out[area].sort((a, b) => a.index - b.index)
        return out
    }, [fields])

    const byName = useMemo(() => new Map(fields.map(f => [f.name, f])), [fields])

    // A keyboard move unmounts the chip from one zone and mounts a new one in another, so the focus
    // has to be put back by hand — otherwise the first arrow key drops the user at the top of the
    // document and the bar is keyboard-hostile after exactly one keystroke. Keyed on `fields`,
    // because that is what changes once the page has actually applied the move.
    useEffect(() => {
        const name = pendingFocus.current
        if (!name) return
        pendingFocus.current = null
        chips.current.get(name)?.focus()
    }, [fields])

    const clearDrag = () => { setDragging(null); setDropArea(null); setDragOverName(null) }

    /** Whether the field currently in the air may land in `area`. */
    const canDrop = (area: BossFieldArea) => {
        if (busy || !dragging) return false
        const field = byName.get(dragging)
        return !!field && areasFor(field).includes(area)
    }

    /** True when dropping `dragged` on `before` in `area` would leave the bar looking identical. */
    const isNoOp = (dragged: BossGridField, area: BossFieldArea, before: string | null) => {
        if (dragged.area !== area) return false
        const order = byArea[area].map(f => f.name)
        const rest = order.filter(name => name !== dragged.name)
        const at = before === null ? -1 : rest.indexOf(before)
        const next = at < 0
            ? [...rest, dragged.name]
            : [...rest.slice(0, at), dragged.name, ...rest.slice(at)]
        return next.every((name, i) => name === order[i])
    }

    const commit = (area: BossFieldArea, before: string | null) => {
        const name = dragging
        clearDrag()
        if (!name || busy) return

        const dragged = byName.get(name)
        if (!dragged || !areasFor(dragged).includes(area)) return
        if (before === name) return
        // Available is unordered — `moveField` ignores the index there and `inventory` re-derives
        // the zone alphabetically — so shuffling within it changes nothing, yet would mark the
        // session dirty and pop Save over a chip that springs straight back.
        if (area === 'available' && dragged.area === 'available') return
        // A drop back onto the place a chip already occupied would still cost a restore(), a full
        // grid rebuild and a dirty session — for a layout nobody changed.
        if (isNoOp(dragged, area, before)) return

        onMove(name, area, dropIndex(byArea[area], dragged, before))
    }

    const onChipKeyDown = (e: KeyboardEvent<HTMLDivElement>, field: BossGridField) => {
        if (busy) return
        const allowed = areasFor(field)
        const at = allowed.indexOf(field.area)

        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            if (at < 0) return
            const target = allowed[at + (e.key === 'ArrowLeft' ? -1 : 1)]
            if (!target) return
            e.preventDefault()
            pendingFocus.current = field.name
            // Append: a keystroke has no cursor to insert at, and the end of the zone is where a
            // drag onto empty space lands too.
            onMove(field.name, target, dropIndex(byArea[target], field, null))
            return
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            // Beyond the four-area move the contract asks for, because the ORDER of the Rows zone
            // IS the report — store-then-brand reads nothing like brand-then-store — and without
            // this a keyboard user could choose the areas but never the nesting.
            //
            // Available is exempt: it is unordered and re-derived alphabetically, so there is
            // nothing for these keys to move.
            if (field.area === 'available') return
            const list = byArea[field.area]
            const from = list.findIndex(f => f.name === field.name)
            const up = e.key === 'ArrowUp'
            if (from < 0 || (up ? from === 0 : from === list.length - 1)) return
            e.preventDefault()
            pendingFocus.current = field.name
            // Up: land in front of the chip above. Down: in front of the one after the next, or at
            // the end of the zone when there is none.
            const before = up ? list[from - 1].name : (list[from + 2]?.name ?? null)
            onMove(field.name, field.area, dropIndex(list, field, before))
        }
    }

    return (
        <div className={`boss-fieldbar${busy ? ' busy' : ''}`} role="group" aria-label="Field arrangement">
            <p id={hintId} className="sr-only">
                Drag a field onto another area, or focus a field and press the left and right arrow
                keys to move it between areas, or the up and down arrow keys to reorder it inside
                its area.
            </p>

            {ZONES.map(zone => {
                const list = byArea[zone.area]
                const inert = dragging !== null && !canDrop(zone.area)

                return (
                    <div
                        key={zone.area}
                        className={'boss-fieldbar-zone'
                            + (dropArea === zone.area ? ' drop-target' : '')
                            + (inert ? ' inert' : '')
                            + (list.length === 0 ? ' empty' : '')}
                        onDragOver={e => {
                            if (!canDrop(zone.area)) return
                            e.preventDefault()
                            e.dataTransfer.dropEffect = 'move'
                            setDropArea(zone.area)
                            setDragOverName(null)
                        }}
                        onDragLeave={() => setDropArea(cur => (cur === zone.area ? null : cur))}
                        onDrop={e => { e.preventDefault(); commit(zone.area, null) }}
                    >
                        <div className="boss-fieldbar-zone-label">{zone.label}</div>

                        <div className="boss-fieldbar-list" role="list" aria-label={`${zone.label} area`}>
                            {list.map(field => (
                                <div
                                    key={field.name}
                                    ref={el => {
                                        if (el) chips.current.set(field.name, el)
                                        else chips.current.delete(field.name)
                                    }}
                                    className={'boss-fieldbar-chip'
                                        + (dragging === field.name ? ' dragging' : '')
                                        + (dragOverName === field.name && dragging !== field.name ? ' drag-over' : '')
                                        + (zone.area === 'group_by' && rowPins && !isRowFieldPinned(rowPins, field.name) ? ' unpinned' : '')}
                                    role="listitem"
                                    tabIndex={0}
                                    draggable={!busy}
                                    title={`${field.name} (${field.dataType})`}
                                    aria-label={`${field.name}, ${zone.label}${field.isDerived ? ', calculated' : ''}`}
                                    aria-describedby={hintId}
                                    onKeyDown={e => onChipKeyDown(e, field)}
                                    onDragStart={e => {
                                        setDragging(field.name)
                                        e.dataTransfer.effectAllowed = 'move'
                                        // Firefox refuses to begin a drag whose dataTransfer is
                                        // empty; this payload exists only to satisfy it. The drag
                                        // state that matters is React's, as in ShortcutPanel.
                                        e.dataTransfer.setData('text/plain', field.name)
                                    }}
                                    onDragEnd={clearDrag}
                                    onDragOver={e => {
                                        if (!canDrop(zone.area)) return
                                        // Stop here so the zone does not light up as well: a drop
                                        // on a chip inserts BEFORE it, a drop on the zone appends.
                                        e.preventDefault()
                                        e.stopPropagation()
                                        e.dataTransfer.dropEffect = 'move'
                                        setDropArea(null)
                                        setDragOverName(field.name)
                                    }}
                                    onDragLeave={() => setDragOverName(cur => (cur === field.name ? null : cur))}
                                    onDrop={e => { e.preventDefault(); e.stopPropagation(); commit(zone.area, field.name) }}
                                >
                                    <span className="boss-fieldbar-grip" aria-hidden="true">⠿</span>
                                    <span className="boss-fieldbar-name">{field.name}</span>

                                    {field.isDerived && (
                                        <span
                                            className="boss-fieldbar-derived"
                                            title="Calculated column: it can only sit in Values, where it keeps its weighted-mean summary"
                                        >fx</span>
                                    )}

                                    {/* Pinning is a Rows concern only: it decides whether this row
                                        field is held at the left or scrolls with the data. It never
                                        moves the field out of Rows — the rows themselves are the
                                        same either way. */}
                                    {zone.area === 'group_by' && rowPins && onPin && (() => {
                                        const pinned = isRowFieldPinned(rowPins, field.name)
                                        return (
                                            <button
                                                type="button"
                                                className="boss-fieldbar-pin"
                                                aria-pressed={pinned}
                                                aria-label={pinned ? `Unpin ${field.name}` : `Pin ${field.name}`}
                                                title={pinned
                                                    ? 'Held in place at the left. Click to let it scroll with the data.'
                                                    : 'Scrolls with the data. Click to hold it in place at the left.'}
                                                disabled={busy}
                                                // The chip owns the arrow keys and the drag; a click here must
                                                // be a click here and nothing more.
                                                onKeyDown={e => e.stopPropagation()}
                                                onMouseDown={e => e.stopPropagation()}
                                                onClick={e => { e.stopPropagation(); onPin(field.name, !pinned) }}
                                            >{pinned ? '📌' : '📍'}</button>
                                        )
                                    })()}

                                    {/* No picker on a derived chip, deliberately, and this is the
                                        one place the bar is narrower than contract §5 asks for.
                                        MARGIN/ROI carry a ['weighted mean', [denominator]]
                                        aggregate — the thing that makes them correct at every
                                        level (C16) — which `aggregateOf` reports as null and
                                        `setAggregate` DELETES for the "Default" option. Offering
                                        the select would hand the user a control whose every
                                        setting, "Default" included, silently turns a verified
                                        weighted mean into a plain one. Summing a ratio is
                                        meaningless anyway. */}
                                    {zone.area === 'columns' && !field.isDerived && (
                                        <select
                                            className="form-select boss-fieldbar-agg"
                                            aria-label={`Summary for ${field.name}`}
                                            disabled={busy}
                                            value={field.aggregate ?? ''}
                                            // The arrow keys belong to the select while it has
                                            // focus; without this the chip would read them too and
                                            // fling the field into the next area mid-choice.
                                            onKeyDown={e => e.stopPropagation()}
                                            onChange={e => onAggregate(
                                                field.name,
                                                BOSS_AGGREGATES.find(a => a === e.target.value) ?? null,
                                            )}
                                        >
                                            <option value="">Default</option>
                                            {BOSS_AGGREGATES.map(a => (
                                                <option key={a} value={a}>{AGGREGATE_LABELS[a]}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            ))}
                        </div>

                        {list.length === 0 && <div className="boss-fieldbar-empty">Drop a field here</div>}
                    </div>
                )
            })}
        </div>
    )
}
